import type { Attachment, Scalars } from '@aurora/shared-generated/types/graphql-schema-types';
import type { FormattedMessageWithValues } from '../../components/i18n/types';

/**
 * An option in the attachment editor or in an attachment list.
 */
class AttachmentOption {
  /**
   * The id of the attachment.
   */
  id: Scalars['ID']['output'];

  /**
   * The temp upload key.
   */
  key?: string;

  /**
   * The attachment's name.
   */
  name?: string;

  /**
   * The attachment's size.
   */
  size?: number;

  /**
   * The attachment's content type.
   */
  contentType?: string;

  /**
   * The attachment's url.  Should be nullish for uploads since they are not available for download yet.
   */
  url?: string;

  /**
   * Flag to show the loading indicator.
   */
  loading: boolean;

  /**
   * Flag to indicate if loading was cancelled
   */
  loadingCancelled: boolean;

  /**
   * Controller used to cancel upload
   */
  abortController: AbortController | null;

  /**
   * Validation errors to display for this attachment.
   */
  errors?: FormattedMessageWithValues[];

  /**
   * Used to allow for duplicate files so they have something unique that busts the
   * equality check done in `isEqual`.
   */
  dedupeId: string | undefined;

  constructor(
    name?: string,
    size?: number,
    contentType?: string,
    id?: Scalars['ID']['output'],
    url?: string,
    dedupeId?: string
  ) {
    this.name = name;
    this.size = size;
    this.contentType = contentType;
    this.id = id ?? `${name}-${size}${dedupeId ? `-${dedupeId}` : ''}`;
    this.url = url;
    this.loading = false;
    this.loadingCancelled = false;
    this.abortController = null;
    this.dedupeId = dedupeId;
  }

  /**
   * Set the temp upload's key.
   * @param key a key value.
   */
  setKey(key: string): AttachmentOption {
    this.key = key;
    return this;
  }

  /**
   * Set the option as loading or not.
   * @param loading whether or not this option is loading.
   */
  setLoading(loading: boolean): AttachmentOption {
    this.loading = loading;
    return this;
  }

  /**
   * Set the flag as cancelled.
   * @param loadingCancelled whether or not the loading for this option is cancelled.
   */
  setLoadingCancelled(loadingCancelled: boolean): AttachmentOption {
    this.loadingCancelled = loadingCancelled;
    return this;
  }

  /**
   * Set the AbortController (https://developer.mozilla.org/en-US/docs/Web/API/AbortController)
   * that can be used to cancel loading
   * @param abortController
   */
  setAbortController(abortController: AbortController): AttachmentOption {
    this.abortController = abortController;
    return this;
  }

  /**
   * Set errors on this option
   * @param errors errors
   */
  setErrors(errors: FormattedMessageWithValues[]): AttachmentOption {
    this.errors = errors;
    return this;
  }

  /**
   * Does this option have any errors?
   */
  hasErrors(): boolean {
    return !!this.errors && this.errors.length > 0;
  }

  static dedupeOption({ name, size, contentType }: AttachmentOption) {
    return new AttachmentOption(
      name,
      size,
      contentType,
      undefined,
      undefined,
      Date.now().toString()
    );
  }
}

/**
 * Does one option equal another?
 * @param a
 * @param b
 */
function isEqual(a: AttachmentOption, b: AttachmentOption): boolean {
  return (
    (a.id && b.id && a.id === b.id) ||
    (a.name &&
      b.name &&
      a.name === b.name &&
      a.size === b.size &&
      a.hasErrors() === b.hasErrors() &&
      a.dedupeId === b.dedupeId)
  );
}

/**
 * Convert an attachment or attachment option, or if an attachment option is presented, return it.
 * @param a
 */
function asAttachmentOption(a: Partial<Attachment> | AttachmentOption): AttachmentOption {
  if ((a as AttachmentOption).loading !== undefined) {
    return a as AttachmentOption;
  } else {
    const { filename, filesize, contentType, id, url } = a as Partial<Attachment>;
    return new AttachmentOption(
      filename ?? undefined,
      filesize ?? undefined,
      contentType ?? undefined,
      id,
      url ?? undefined
    );
  }
}
export default AttachmentOption;
export { isEqual, asAttachmentOption };
